/**
 * `prompt_job_outputs.input_metadata` is a jsonb object in the database, but the
 * data-retriever API projects it via `CAST(... AS text)`, so it arrives on the wire
 * as a JSON **string** (or null). Shape:
 *
 *   { "<source label>": [ { ...per-input metadata, e.g. call_sid, created_on }, ... ] }
 *
 * These helpers are deliberately total: no input — null, malformed JSON, an
 * unexpected shape — may throw, because a single bad value must never blank the
 * whole AI Outputs tab (KAN-2488).
 */

/** Parse the wire value, returning null instead of throwing on anything unexpected. */
export const parseInputMetadata = (raw: string | null | undefined): unknown => {
	if (!raw) return null;
	try {
		return JSON.parse(raw);
	} catch {
		return null;
	}
};

/** Pretty-printed JSON for display, falling back to the raw string. */
export const prettyInputMetadata = (raw: string | null | undefined): string => {
	if (!raw) return "";
	const parsed = parseInputMetadata(raw);
	return parsed === null ? raw : JSON.stringify(parsed, null, 2);
};

/**
 * One-line provenance summary: which source(s) produced the output, how many
 * inputs each contributed, and the first timestamp found.
 * e.g. `conversation (2), document (1) (2026-08-01T10:00:00)`
 */
export const formatInputMetadata = (raw: string | null | undefined): string => {
	if (!raw) return "";

	const parsed = parseInputMetadata(raw);
	if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
		// Not the documented object shape — show the value verbatim rather than lying.
		return raw;
	}

	const parts: string[] = [];
	let firstDate: string | undefined;

	for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
		if (Array.isArray(value)) {
			parts.push(`${key} (${value.length})`);
			if (!firstDate) {
				for (const entry of value) {
					if (typeof entry === "object" && entry !== null) {
						const record = entry as Record<string, unknown>;
						const date = record.created_on ?? record.createdOn;
						if (date) {
							firstDate = String(date);
							break;
						}
					}
				}
			}
		} else {
			parts.push(key);
		}
	}

	const summary = parts.join(", ");
	return firstDate ? `${summary} (${firstDate})` : summary;
};
